import { useState, useEffect, FormEvent } from 'react';

interface RegistrationFormsProps {
  selectedCourseVoucher: string;
  selectedCarVoucher: string;
}

export default function RegistrationForms({ selectedCourseVoucher, selectedCarVoucher }: RegistrationFormsProps) {
  const [courseVoucher, setCourseVoucher] = useState(selectedCourseVoucher);
  const [carVoucher, setCarVoucher] = useState(selectedCarVoucher);
  const [courseDiscount, setCourseDiscount] = useState(0);
  const [carDiscount, setCarDiscount] = useState(0);
  const [courseSubmitting, setCourseSubmitting] = useState(false);
  const [carSubmitting, setCarSubmitting] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const originalCoursePrice = 3000000;
  const originalCarPrice = 2500000;

  // Update voucher when prop changes
  useEffect(() => {
    if (selectedCourseVoucher) {
      setCourseVoucher(selectedCourseVoucher);
      // Auto apply voucher
      if (selectedCourseVoucher === 'ERB-30') {
        setCourseDiscount(30);
      } else if (selectedCourseVoucher === 'ERB-20') {
        setCourseDiscount(20);
      }
    }
  }, [selectedCourseVoucher]);

  useEffect(() => {
    if (selectedCarVoucher) {
      setCarVoucher(selectedCarVoucher);
      // Auto apply voucher
      if (selectedCarVoucher === 'RBSC-30') {
        setCarDiscount(30);
      } else if (selectedCarVoucher === 'RBSC-20') {
        setCarDiscount(20);
      }
    }
  }, [selectedCarVoucher]);

  const applyCourseVoucher = () => {
    if (courseVoucher === 'ERB-30') {
      setCourseDiscount(30);
    } else if (courseVoucher === 'ERB-20') {
      setCourseDiscount(20);
    } else {
      alert('Mã voucher không hợp lệ!');
      setCourseDiscount(0);
    }
  };

  const applyCarVoucher = () => {
    if (carVoucher === 'RBSC-30') {
      setCarDiscount(30);
    } else if (carVoucher === 'RBSC-20') {
      setCarDiscount(20);
    } else {
      alert('Mã voucher không hợp lệ!');
      setCarDiscount(0);
    }
  };

  const handleCourseSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCourseSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d5ht7t3ihlvellpk562g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Đăng ký khóa học thành công! Chúng tôi sẽ liên hệ với bạn sớm.');
        e.currentTarget.reset();
        setCourseDiscount(0);
        setCourseVoucher('');
      } else {
        alert('Có lỗi xảy ra. Vui lòng thử lại!');
      }
    } catch (error) {
      alert('Có lỗi xảy ra. Vui lòng thử lại!');
    } finally {
      setCourseSubmitting(false);
    }
  };

  const handleCarSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCarSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d5ht7t3ihlvellpk5630', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Đặt hàng thành công! Chúng tôi sẽ liên hệ với bạn sớm.');
        e.currentTarget.reset();
        setCarDiscount(0);
        setCarVoucher('');
        setQuantity(1);
      } else {
        alert('Có lỗi xảy ra. Vui lòng thử lại!');
      }
    } catch (error) {
      alert('Có lỗi xảy ra. Vui lòng thử lại!');
    } finally {
      setCarSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Course Registration Form */}
          <div id="course-form" className="scroll-mt-24">
            <div className="mb-8">
              <h2 className="text-4xl font-bold text-blue-900 mb-3 flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <i className="ri-graduation-cap-fill text-2xl text-blue-600"></i>
                </div>
                Đăng Ký Khóa Học Robotics
              </h2>
              <p className="text-gray-600">Điền thông tin để nhận tư vấn chi tiết về khóa học</p>
            </div>

            <form onSubmit={handleCourseSubmit} data-readdy-form className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Họ và Tên <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="fullname"
                  required
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="Nguyễn Văn A"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                <input 
                  type="email" 
                  name="email"
                  required
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Số Điện Thoại <span className="text-red-500">*</span></label>
                <input 
                  type="tel" 
                  name="phone"
                  required
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="0912345678"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Độ Tuổi Học Viên <span className="text-red-500">*</span></label>
                <select 
                  name="age"
                  required
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer"
                >
                  <option value="">Chọn độ tuổi</option>
                  <option value="6-8">6-8 tuổi</option>
                  <option value="9-11">9-11 tuổi</option>
                  <option value="12-14">12-14 tuổi</option>
                  <option value="15-17">15-17 tuổi</option>
                  <option value="18+">18+ tuổi</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mã Voucher</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <i className="ri-price-tag-3-fill absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input 
                      type="text" 
                      name="voucher"
                      value={courseVoucher}
                      onChange={(e) => setCourseVoucher(e.target.value.toUpperCase())}
                      className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      placeholder="ERB-30 hoặc ERB-20"
                    />
                  </div>
                  <button 
                    type="button"
                    onClick={applyCourseVoucher}
                    className="px-6 py-3.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Áp Dụng
                  </button>
                </div>
                {courseDiscount > 0 && (
                  <p className="mt-2 text-sm text-green-600 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill"></i>
                    Đã áp dụng giảm giá {courseDiscount}%
                  </p>
                )}
              </div>

              {courseDiscount > 0 && (
                <div className="bg-blue-50 rounded-xl p-5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Giá gốc:</span>
                    <span className="text-gray-400 line-through">{originalCoursePrice.toLocaleString('vi-VN')}đ</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Giảm giá:</span>
                    <span className="text-green-600 font-semibold">-{(originalCoursePrice * courseDiscount / 100).toLocaleString('vi-VN')}đ</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-blue-200">
                    <span className="text-lg font-bold text-gray-900">Tổng thanh toán:</span>
                    <span className="text-2xl font-bold text-blue-600">{(originalCoursePrice * (100 - courseDiscount) / 100).toLocaleString('vi-VN')}đ</span>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <input 
                  type="checkbox" 
                  id="course-terms"
                  required
                  className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="course-terms" className="text-sm text-gray-600 cursor-pointer">
                  Tôi đồng ý với điều khoản và chính sách của ESAPCOR
                </label>
              </div>

              <button 
                type="submit"
                disabled={courseSubmitting}
                className="w-full py-5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                {courseSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Đang gửi...
                  </>
                ) : (
                  <>
                    ĐĂNG KÝ NGAY
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Smart Car Purchase Form */}
          <div id="car-form" className="scroll-mt-24">
            <div className="mb-8">
              <h2 className="text-4xl font-bold text-orange-600 mb-3 flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <i className="ri-car-fill text-2xl text-orange-600"></i>
                </div>
                Mua Bộ Smart Car Robotics
              </h2>
              <p className="text-gray-600">Sở hữu ngay bộ kit robotics chất lượng cao</p>
            </div>

            <div className="mb-8 relative rounded-2xl overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/9c9df5e92c1cb80290a5328b0616f88c.jpg"
                alt="Smart Car Robotics"
                className="w-full h-80 object-cover object-top"
              />
              <span className="absolute top-4 right-4 px-4 py-2 bg-red-500 text-white rounded-full font-bold text-sm whitespace-nowrap">
                BÁN CHẠY
              </span>
            </div>

            <div className="mb-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-2xl text-gray-400 line-through">{originalCarPrice.toLocaleString('vi-VN')}đ</span>
                {carDiscount > 0 && (
                  <span className="text-4xl font-bold text-orange-600">
                    {(originalCarPrice * (100 - carDiscount) / 100).toLocaleString('vi-VN')}đ
                  </span>
                )}
              </div>
              {carDiscount > 0 && (
                <p className="text-green-600 font-semibold">
                  Tiết kiệm: {(originalCarPrice * carDiscount / 100).toLocaleString('vi-VN')}đ
                </p>
              )}
            </div>

            <form onSubmit={handleCarSubmit} data-readdy-form className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Họ và Tên <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="fullname"
                  required
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                  placeholder="Nguyễn Văn A"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                <input 
                  type="email" 
                  name="email"
                  required
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Số Điện Thoại <span className="text-red-500">*</span></label>
                <input 
                  type="tel" 
                  name="phone"
                  required
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                  placeholder="0912345678"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Số Lượng <span className="text-red-500">*</span></label>
                <div className="flex items-center gap-4">
                  <button 
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 bg-gray-100 rounded-lg font-bold text-xl hover:bg-gray-200 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    -
                  </button>
                  <input 
                    type="number" 
                    name="quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    required
                    className="flex-1 px-4 py-3.5 border border-gray-300 rounded-lg text-center font-bold text-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                  />
                  <button 
                    type="button"
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 bg-gray-100 rounded-lg font-bold text-xl hover:bg-gray-200 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    +
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mã Voucher</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <i className="ri-price-tag-3-fill absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input 
                      type="text" 
                      name="voucher"
                      value={carVoucher}
                      onChange={(e) => setCarVoucher(e.target.value.toUpperCase())}
                      className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                      placeholder="RBSC-30 hoặc RBSC-20"
                    />
                  </div>
                  <button 
                    type="button"
                    onClick={applyCarVoucher}
                    className="px-6 py-3.5 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Áp Dụng
                  </button>
                </div>
                {carDiscount > 0 && (
                  <p className="mt-2 text-sm text-green-600 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill"></i>
                    Đã áp dụng giảm giá {carDiscount}%
                  </p>
                )}
              </div>

              {carDiscount > 0 && (
                <div className="bg-orange-50 rounded-xl p-5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Đơn giá:</span>
                    <span className="text-gray-900 font-semibold">{(originalCarPrice * (100 - carDiscount) / 100).toLocaleString('vi-VN')}đ</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Số lượng:</span>
                    <span className="text-gray-900 font-semibold">x{quantity}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-orange-200">
                    <span className="text-lg font-bold text-gray-900">Tổng thanh toán:</span>
                    <span className="text-2xl font-bold text-orange-600">{(originalCarPrice * (100 - carDiscount) / 100 * quantity).toLocaleString('vi-VN')}đ</span>
                  </div>
                </div>
              )}

              <button 
                type="submit"
                disabled={carSubmitting}
                className="w-full py-5 bg-gradient-to-r from-orange-600 to-red-500 text-white rounded-xl font-bold text-lg hover:from-orange-700 hover:to-red-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                {carSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Đang gửi...
                  </>
                ) : (
                  <>
                    MUA NGAY
                    <i className="ri-shopping-cart-fill"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}